import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import TabPanel from "@mui/joy/TabPanel";
import Table from "@mui/joy/Table";
import Tabs from "@mui/joy/Tabs";
import Typography from "@mui/joy/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    defaultPeriod,
    formatInt,
    formatMoneyKopecks,
    fromDatetimeLocalValue,
    toDatetimeLocalValue,
    toNum,
} from "../../bids/analytics/format";
import { formatWith } from "../../../common/dateTime";
import { Game } from "../../../models/games";
import {
    AdminExternalProviderInfo,
    ExternalMarketErrorKindDescription,
    ExtAnalyticsParams,
    StatsGranularity,
    StatsGranularityDescription,
} from "../../../models/externalMarkets";
import { gamesListApi } from "../../games/gamesApi";
import {
    extByProviderApi,
    extFailuresApi,
    extProvidersApi,
    extSummaryApi,
    extTimeseriesApi,
    extTopItemsApi,
} from "../externalMarketsApi";
import { formatPercent, resolveGameName } from "../components/shared";

type Filters = {
    from: Date;
    to: Date;
    gameId?: number;
    providerKey?: string;
    granularity: StatsGranularity;
    take: number;
};

const TABS = ["overview", "providers", "failures", "top"] as const;

const tabListSx = {
    pl: { xs: 0, md: 2 },
    justifyContent: "left",
    [`&& .${tabClasses.root}`]: {
        fontWeight: "600",
        flex: "initial",
        color: "text.tertiary",
        [`&.${tabClasses.selected}`]: {
            bgcolor: "transparent",
            color: "text.primary",
            "&::after": { height: "2px", bgcolor: "primary.500" },
        },
    },
} as const;

/** Прибыль: отклонение факта от плана красным, если факт меньше */
function Deviation(props: { value: number | string }) {
    const v = toNum(props.value);
    return (
        <Typography level="body-sm" color={v < 0 ? "danger" : v > 0 ? "success" : undefined}>
            {v > 0 ? "+" : ""}
            {formatMoneyKopecks(v)}
        </Typography>
    );
}

function bucketFormat(granularity: StatsGranularity): string {
    switch (granularity) {
        case StatsGranularity.Hour:
            return "DD.MM HH:00";
        case StatsGranularity.Month:
            return "MM.YYYY";
        case StatsGranularity.Year:
            return "YYYY";
        default:
            return "DD.MM.YYYY";
    }
}

export function ExternalMarketAnalytics() {
    const navigate = useNavigate();
    const location = useLocation();
    const tab = Math.max(0, TABS.indexOf(location.hash.replace("#", "") as (typeof TABS)[number]));
    const [filters, setFilters] = React.useState<Filters>(() => {
        const p = defaultPeriod();
        return { from: p.from, to: p.to, granularity: StatsGranularity.Day, take: 20 };
    });
    const [games, setGames] = React.useState<Game[]>([]);
    const [providers, setProviders] = React.useState<AdminExternalProviderInfo[]>([]);
    const [version, setVersion] = React.useState(0);

    React.useEffect(() => {
        gamesListApi()
            .then((res) => res.result && setGames(res.value ?? []))
            .catch(console.error);
        extProvidersApi()
            .then((res) => res.result && setProviders(res.value ?? []))
            .catch(console.error);
    }, []);

    const params: ExtAnalyticsParams = {
        from: filters.from,
        to: filters.to,
        gameId: filters.gameId,
        providerKey: filters.providerKey,
        granularity: filters.granularity,
        take: filters.take,
    };
    const deps = [filters.from, filters.to, filters.gameId, filters.providerKey, filters.granularity, filters.take, version];
    const marketGames = Array.from(new Set(providers.flatMap((p) => p.markets.map((m) => m.gameId))));

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: { xs: 2, md: 6 },
                pt: { xs: "calc(12px + var(--Header-height))", sm: "calc(12px + var(--Header-height))", md: 3 },
                pb: { xs: 2, sm: 2, md: 3 },
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                height: "100dvh",
                gap: 1,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography level="h2" component="h1">
                    Аналитика поставщиков
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="sm" variant="outlined" startDecorator={<ArrowBackIcon />} onClick={() => navigate("/app/external-markets")}>
                    Управление поставщиками
                </Button>
            </Stack>

            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: "background.level1",
                    borderRadius: "md",
                }}
            >
                <FormControl size="sm">
                    <FormLabel>С</FormLabel>
                    <Input
                        type="datetime-local"
                        value={toDatetimeLocalValue(filters.from)}
                        onChange={(e) => e.target.value && setFilters({ ...filters, from: fromDatetimeLocalValue(e.target.value) })}
                    />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>По</FormLabel>
                    <Input
                        type="datetime-local"
                        value={toDatetimeLocalValue(filters.to)}
                        onChange={(e) => e.target.value && setFilters({ ...filters, to: fromDatetimeLocalValue(e.target.value) })}
                    />
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Игра</FormLabel>
                    <Select
                        size="sm"
                        value={filters.gameId ?? null}
                        onChange={(_, v) => setFilters({ ...filters, gameId: typeof v === "number" ? v : undefined })}
                    >
                        <Option value={null}>Все игры</Option>
                        {(marketGames.length > 0 ? marketGames : games.map((g) => g.id)).map((id) => (
                            <Option key={id} value={id}>
                                {resolveGameName(games, id)}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Поставщик</FormLabel>
                    <Select
                        size="sm"
                        value={filters.providerKey ?? null}
                        onChange={(_, v) => setFilters({ ...filters, providerKey: (v as string) || undefined })}
                    >
                        <Option value={null}>Все поставщики</Option>
                        {providers.map((p) => (
                            <Option key={p.key} value={p.key}>
                                {p.name}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 130 }}>
                    <FormLabel>Шаг графика</FormLabel>
                    <Select
                        size="sm"
                        value={filters.granularity}
                        onChange={(_, v) => setFilters({ ...filters, granularity: (v as StatsGranularity) ?? StatsGranularity.Day })}
                    >
                        {[StatsGranularity.Hour, StatsGranularity.Day, StatsGranularity.Month, StatsGranularity.Year].map((g) => (
                            <Option key={g} value={g}>
                                {StatsGranularityDescription[g]}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{ width: 90 }}>
                    <FormLabel>Топ</FormLabel>
                    <Input
                        type="number"
                        value={filters.take}
                        onChange={(e) =>
                            setFilters({ ...filters, take: Math.max(1, Math.min(100, Number(e.target.value) || 20)) })
                        }
                    />
                </FormControl>
                <Button size="sm" variant="outlined" startDecorator={<RefreshIcon />} onClick={() => setVersion((v) => v + 1)}>
                    Обновить
                </Button>
            </Box>
            <Typography level="body-xs" sx={{ px: 0.5 }}>
                Продажи — заказы в статусах «Завершён» и «Возврат», как в статистике заказов и юнит-экономике. Фильтр
                поставщика сужает внешние покупки, но не общий оборот. Сводка «Доля и прибыль» строится по всем играм и поставщикам;
                фильтр игры действует на график и вкладку «Поставщики», фильтр поставщика — на график, провалы и популярные предметы.
            </Typography>

            <Tabs
                value={tab}
                onChange={(_, v) => navigate({ hash: `#${TABS[typeof v === "number" ? v : 0]}` })}
                sx={{ bgcolor: "transparent" }}
            >
                <TabList size="sm" sx={tabListSx}>
                    <Tab value={0}>Доля и прибыль</Tab>
                    <Tab value={1}>Поставщики</Tab>
                    <Tab value={2}>Провалы выкупа</Tab>
                    <Tab value={3}>Популярные предметы</Tab>
                </TabList>
                <TabPanel value={0} sx={{ px: 0 }}>
                    <OverviewSection active={tab === 0} params={params} deps={deps} games={games} />
                </TabPanel>
                <TabPanel value={1} sx={{ px: 0 }}>
                    <ProvidersSection active={tab === 1} params={params} deps={deps} games={games} />
                </TabPanel>
                <TabPanel value={2} sx={{ px: 0 }}>
                    <FailuresSection active={tab === 2} params={params} deps={deps} />
                </TabPanel>
                <TabPanel value={3} sx={{ px: 0 }}>
                    <TopItemsSection active={tab === 3} params={params} deps={deps} games={games} />
                </TabPanel>
            </Tabs>
        </Box>
    );
}

type SectionProps = {
    active: boolean;
    params: ExtAnalyticsParams;
    deps: React.DependencyList;
    games?: Game[];
};

function OverviewSection(props: SectionProps) {
    const { active, params, deps, games = [] } = props;
    const [summary, refreshSummary] = useAsyncBlock(() => extSummaryApi(params), active, deps);
    const [series, refreshSeries] = useAsyncBlock(() => extTimeseriesApi(params), active, deps);
    const rows = summary.data ?? [];
    const all = rows.find((r) => r.gameId === undefined || r.gameId === null);
    const byGame = rows.filter((r) => r.gameId !== undefined && r.gameId !== null);
    const format = bucketFormat(params.granularity ?? StatsGranularity.Day);
    const points = (series.data ?? []).map((p) => ({
        x: formatWith(p.bucketUtc, format),
        total: toNum(p.totalSum) / 100,
        external: toNum(p.externalSum) / 100,
        share: toNum(p.share),
    }));

    return (
        <Stack spacing={2}>
            {summary.loading && !summary.data && <BlockLoading />}
            {summary.error && <BlockError message={summary.error} onRetry={refreshSummary} />}
            {all && (
                <AnalyticsCard
                    title="Доля покупок через поставщиков"
                    subtitle="Сколько продаж площадки прошло через поставщиков и сколько на них заработано: план — наценка на витрине, факт — цена минус уплаченное поставщику."
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>Доля по сумме важнее доли по штукам: поставщики часто закрывают дорогие позиции.</ListItem>
                            <ListItem>
                                Факт считается только по заказам с прошедшим выкупом; разница с планом — изменение цены или курса
                                между витриной и выкупом.
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: "Доля по сумме",
                                    ifGrows: "поставщики закрывают спрос, которого нет у живых продавцов.",
                                    ifFalls: "витрина поставщика пустеет или цены неконкурентны — проверьте синхронизацию и правила прибыли.",
                                },
                                {
                                    signal: "Факт минус план",
                                    ifGrows: "выкупаем дешевле, чем заложено в цену.",
                                    ifFalls: "цены у поставщика растут между синхронизацией и выкупом — сузьте допуски или поднимите наценку.",
                                },
                            ]}
                        />
                    }
                >
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 1.5 }}>
                        <MetricHint
                            label="Доля по сумме"
                            value={formatPercent(all.shareBySum)}
                            hint={`${formatMoneyKopecks(all.externalSum)} из ${formatMoneyKopecks(all.totalSum)}`}
                            color="primary"
                        />
                        <MetricHint
                            label="Доля по штукам"
                            value={formatPercent(all.shareByCount)}
                            hint={`${formatInt(all.externalCount)} из ${formatInt(all.totalCount)}`}
                        />
                        <MetricHint label="Уплачено поставщикам" value={formatMoneyKopecks(all.costRub)} />
                        <MetricHint label="Прибыль план" value={formatMoneyKopecks(all.plannedProfitRub)} />
                        <MetricHint
                            label="Прибыль факт"
                            value={formatMoneyKopecks(all.actualProfitRub)}
                            hint={<Deviation value={all.profitDeviationRub} />}
                            color={toNum(all.profitDeviationRub) < 0 ? "danger" : "success"}
                        />
                    </Stack>

                    <Typography level="title-sm" sx={{ mt: 2 }}>
                        По играм
                    </Typography>
                    {byGame.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <ScrollTable>
                            <Table size="sm" hoverRow>
                                <thead>
                                    <tr>
                                        <th>Игра</th>
                                        <th style={{ textAlign: "right" }}>Продажи</th>
                                        <th style={{ textAlign: "right" }}>Через поставщиков</th>
                                        <th style={{ textAlign: "right" }}>Доля шт / ₽</th>
                                        <th style={{ textAlign: "right" }}>Уплачено</th>
                                        <th style={{ textAlign: "right" }}>План</th>
                                        <th style={{ textAlign: "right" }}>Факт</th>
                                        <th style={{ textAlign: "right" }}>Факт − план</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byGame.map((r) => (
                                        <tr key={r.gameId}>
                                            <td>{r.gameName || resolveGameName(games, r.gameId)}</td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatInt(r.totalCount)} / {formatMoneyKopecks(r.totalSum)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatInt(r.externalCount)} / {formatMoneyKopecks(r.externalSum)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatPercent(r.shareByCount)} / {formatPercent(r.shareBySum)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.costRub)}</td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.plannedProfitRub)}</td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.actualProfitRub)}</td>
                                            <td style={{ textAlign: "right" }}>
                                                <Deviation value={r.profitDeviationRub} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    )}
                </AnalyticsCard>
            )}

            <AnalyticsCard
                title="Продажи во времени"
                subtitle="Общий оборот и оборот через поставщиков по выбранному шагу; доля — по сумме."
                snapshotLabel="period"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>Фильтр поставщика меняет только внешние продажи: общий оборот остаётся по всем продавцам.</ListItem>
                    </List>
                }
            >
                {series.loading && !series.data && <BlockLoading />}
                {series.error && <BlockError message={series.error} onRetry={refreshSeries} />}
                {series.data &&
                    (points.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <>
                            <BarChart
                                height={280}
                                dataset={points}
                                xAxis={[{ dataKey: "x", scaleType: "band" }]}
                                series={[
                                    { dataKey: "total", label: "Все продажи, ₽", color: "#90caf9" },
                                    { dataKey: "external", label: "Через поставщиков, ₽", color: "#1976d2" },
                                ]}
                                margin={{ left: 80, right: 20, top: 20, bottom: 50 }}
                            />
                            <LineChart
                                height={220}
                                dataset={points}
                                xAxis={[{ dataKey: "x", scaleType: "point" }]}
                                series={[
                                    {
                                        dataKey: "share",
                                        label: "Доля поставщиков, %",
                                        color: "#ed6c02",
                                        valueFormatter: (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`),
                                    },
                                ]}
                                margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                            />
                        </>
                    ))}
            </AnalyticsCard>
        </Stack>
    );
}

function ProvidersSection(props: SectionProps) {
    const { active, params, deps, games = [] } = props;
    const [state, refresh] = useAsyncBlock(() => extByProviderApi(params), active, deps);
    const rows = (state.data ?? []).filter((r) => !params.providerKey || r.providerKey === params.providerKey);
    const totals = rows.filter((r) => r.gameId === undefined || r.gameId === null);
    const perGame = rows.filter((r) => r.gameId !== undefined && r.gameId !== null);

    return (
        <AnalyticsCard
            title="Поставщики"
            subtitle="Вклад каждого поставщика во внешние продажи, наценка по факту и надёжность выкупа."
            snapshotLabel="period"
            howToRead={
                <List size="sm" marker="disc">
                    <ListItem>Доля среди внешних — какую часть внешних продаж по сумме дал поставщик.</ListItem>
                    <ListItem>Средняя наценка — фактическая прибыль к себестоимости по прошедшим выкупам.</ListItem>
                    <ListItem>Провалы — доля неудачных выкупов среди всех попыток; деньги покупателям возвращены.</ListItem>
                </List>
            }
            watchFor={
                <WatchSignals
                    items={[
                        {
                            signal: "Провалы выкупа",
                            ifGrows: "покупатели получают отказы — смотрите причины на вкладке «Провалы выкупа».",
                            ifFalls: "поставщик работает стабильно.",
                            growsIsGood: false,
                        },
                    ]}
                />
            }
        >
            {state.loading && !state.data && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {state.data &&
                (rows.length === 0 ? (
                    <EmptyState />
                ) : (
                    <ScrollTable>
                        <Table size="sm" hoverRow>
                            <thead>
                                <tr>
                                    <th>Поставщик / игра</th>
                                    <th style={{ textAlign: "right" }}>Продажи</th>
                                    <th style={{ textAlign: "right" }}>Доля в обороте</th>
                                    <th style={{ textAlign: "right" }}>Доля среди внешних</th>
                                    <th style={{ textAlign: "right" }}>Уплачено</th>
                                    <th style={{ textAlign: "right" }}>Прибыль факт</th>
                                    <th style={{ textAlign: "right" }}>Ср. наценка</th>
                                    <th style={{ textAlign: "right" }}>Провалы</th>
                                </tr>
                            </thead>
                            <tbody>
                                {totals.map((t) => (
                                    <React.Fragment key={t.providerKey}>
                                        <tr>
                                            <td>
                                                <Typography level="title-sm">{t.providerName}</Typography>
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatInt(t.externalCount)} / {formatMoneyKopecks(t.externalSum)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>{formatPercent(t.shareBySum)}</td>
                                            <td style={{ textAlign: "right" }}>{formatPercent(t.shareOfExternal)}</td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(t.costRub)}</td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(t.actualProfitRub)}</td>
                                            <td style={{ textAlign: "right" }}>{formatPercent(t.avgProfitPercent)}</td>
                                            <td style={{ textAlign: "right" }}>
                                                <Typography level="body-sm" color={t.failedCount > 0 ? "danger" : undefined}>
                                                    {formatInt(t.failedCount)} ({formatPercent(t.failedRate)})
                                                </Typography>
                                            </td>
                                        </tr>
                                        {perGame
                                            .filter((g) => g.providerKey === t.providerKey)
                                            .map((g) => (
                                                <tr key={`${g.providerKey}-${g.gameId}`}>
                                                    <td style={{ paddingLeft: 24 }}>
                                                        <Typography level="body-sm">
                                                            {g.gameName || resolveGameName(games, g.gameId)}
                                                        </Typography>
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatInt(g.externalCount)} / {formatMoneyKopecks(g.externalSum)}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>{formatPercent(g.shareBySum)}</td>
                                                    <td style={{ textAlign: "right" }}>{formatPercent(g.shareOfExternal)}</td>
                                                    <td style={{ textAlign: "right" }}>{formatMoneyKopecks(g.costRub)}</td>
                                                    <td style={{ textAlign: "right" }}>{formatMoneyKopecks(g.actualProfitRub)}</td>
                                                    <td style={{ textAlign: "right" }}>{formatPercent(g.avgProfitPercent)}</td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatInt(g.failedCount)} ({formatPercent(g.failedRate)})
                                                    </td>
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </ScrollTable>
                ))}
        </AnalyticsCard>
    );
}

function FailuresSection(props: SectionProps) {
    const { active, params, deps } = props;
    const [state, refresh] = useAsyncBlock(() => extFailuresApi(params), active, deps);
    const rows = state.data ?? [];

    return (
        <AnalyticsCard
            title="Провалы выкупа"
            subtitle="Почему не удалось выкупить предмет у поставщика и сколько продаж потеряно (деньги покупателям возвращены)."
            snapshotLabel="period"
            howToRead={
                <List size="sm" marker="disc">
                    <ListItem>«Нет в наличии» и «Цена вышла за допуск» — витрина отстала от поставщика.</ListItem>
                    <ListItem>«Недостаточно средств» выключает связку и снимает витрину — пополните баланс и включите вручную.</ListItem>
                    <ListItem>«Трейд не прошёл» — выкуп у поставщика оплачен, а трейд на бота не создан или не принят: проверьте историю покупок и трейдов аккаунта поставщика.</ListItem>
                </List>
            }
        >
            {state.loading && !state.data && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {state.data &&
                (rows.length === 0 ? (
                    <EmptyState hint="Провалов за период нет." />
                ) : (
                    <>
                        <BarChart
                            height={240}
                            layout="horizontal"
                            yAxis={[
                                {
                                    data: rows.map((r) => ExternalMarketErrorKindDescription[r.errorKind] ?? String(r.errorKind)),
                                    scaleType: "band",
                                },
                            ]}
                            series={[{ data: rows.map((r) => r.count), label: "Провалов", color: "#d32f2f" }]}
                            margin={{ left: 220, right: 20, top: 20, bottom: 40 }}
                        />
                        <ScrollTable>
                            <Table size="sm" hoverRow>
                                <thead>
                                    <tr>
                                        <th>Причина</th>
                                        <th style={{ textAlign: "right" }}>Провалов</th>
                                        <th style={{ textAlign: "right" }}>Упущенные продажи</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.errorKind}>
                                            <td>{ExternalMarketErrorKindDescription[r.errorKind] ?? r.errorKind}</td>
                                            <td style={{ textAlign: "right" }}>{formatInt(r.count)}</td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.lostSum)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    </>
                ))}
        </AnalyticsCard>
    );
}

function TopItemsSection(props: SectionProps) {
    const { active, params, deps, games = [] } = props;
    const [state, refresh] = useAsyncBlock(() => extTopItemsApi(params), active, deps);
    const rows = state.data ?? [];

    return (
        <AnalyticsCard
            title="Популярные предметы поставщиков"
            subtitle="Что чаще всего покупают через поставщиков и сколько на этом заработано по факту."
            snapshotLabel="period"
            howToRead={
                <List size="sm" marker="disc">
                    <ListItem>Высокий спрос при малой прибыли — кандидаты на отдельное правило прибыли.</ListItem>
                </List>
            }
        >
            {state.loading && !state.data && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {state.data &&
                (rows.length === 0 ? (
                    <EmptyState />
                ) : (
                    <ScrollTable>
                        <Table size="sm" hoverRow>
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>#</th>
                                    <th>Предмет</th>
                                    <th>Игра</th>
                                    <th style={{ textAlign: "right" }}>Продано</th>
                                    <th style={{ textAlign: "right" }}>Сумма</th>
                                    <th style={{ textAlign: "right" }}>Прибыль факт</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={`${r.gameId}-${r.skinName}`}>
                                        <td>{i + 1}</td>
                                        <td>{r.skinName}</td>
                                        <td>{resolveGameName(games, r.gameId)}</td>
                                        <td style={{ textAlign: "right" }}>{formatInt(r.count)}</td>
                                        <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.sum)}</td>
                                        <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.actualProfitRub)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ScrollTable>
                ))}
        </AnalyticsCard>
    );
}

export default ExternalMarketAnalytics;
